import { useEffect, useState } from 'react';
import { brand } from '../../lib/brand';
import { api } from '../../lib/api';
import { _t } from '../../lib/i18n';
import type { Language } from '../../types';

type PublishedReview = {
  guestName: string;
  body: string;
  galleryName: string;
};

const PREVIEW_CHARS = 180;

function reviewPreview(body: string) {
  const text = body.trim();
  if (text.length <= PREVIEW_CHARS) return '';
  const slice = text.slice(0, PREVIEW_CHARS);
  const cut = Math.max(slice.lastIndexOf(' '), slice.lastIndexOf('\n'));
  return (cut > PREVIEW_CHARS * 0.6 ? slice.slice(0, cut) : slice).trimEnd();
}

function ReviewBody({ body, lang }: { body: string; lang: Language }) {
  const preview = reviewPreview(body);
  const [open, setOpen] = useState(false);
  if (!preview) {
    return <p className="whitespace-pre-wrap text-sm text-[var(--text-primary)]">{body}</p>;
  }
  return (
    <p className="whitespace-pre-wrap text-sm text-[var(--text-primary)]">
      {open ? body : `${preview}… `}
      <button
        type="button"
        className={open
          ? 'mt-2 block text-[var(--accent-primary)] underline decoration-[var(--accent-primary)] underline-offset-2'
          : 'text-[var(--accent-primary)] underline decoration-[var(--accent-primary)] underline-offset-2'}
        onClick={() => setOpen((value) => !value)}
      >
        {_t(open ? 'galleries.reviewCollapse' : 'galleries.reviewExpand', lang)}
      </button>
    </p>
  );
}

/** Published guest notes. Empty until the photographer publishes one, and only on Alive Album. */
export function PublishedReviews({ lang, standalone = false }: { lang: Language; standalone?: boolean }) {
  const [rows, setRows] = useState<PublishedReview[]>([]);
  useEffect(() => {
    if (brand.id !== 'alivealbum') return undefined;
    let cancelled = false;
    void api.listPublicReviews()
      .then((data) => {
        if (!cancelled) setRows(data.reviews);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  if (brand.id !== 'alivealbum') return null;
  if (rows.length === 0) {
    if (!standalone) return null;
    return <p className="text-sm text-[var(--text-secondary)]">{_t('galleries.publishedEmpty', lang)}</p>;
  }
  return (
    <section className="flex flex-col gap-3">
      {standalone ? null : (
        <h2 className="text-sm font-semibold text-[var(--text-primary)]">{_t('galleries.reviews', lang)}</h2>
      )}
      <ul className={standalone ? 'flex flex-col gap-4' : 'flex flex-col gap-3'}>
        {rows.map((row, index) => (
          <li
            key={`${row.guestName}-${row.body.slice(0, 24)}`}
            id={standalone ? `public-review-${index}` : undefined}
            className={standalone
              ? 'rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-4 shadow-sm'
              : 'text-sm'}
          >
            <ReviewBody body={row.body} lang={lang} />
            {standalone ? (
              <>
                <p className="mt-2 text-xs font-medium text-[var(--text-primary)]">{row.guestName}</p>
                {row.galleryName ? (
                  <p className="mt-0.5 text-xs text-[var(--text-secondary)]">{row.galleryName}</p>
                ) : null}
              </>
            ) : (
              <p className="mt-1 text-xs text-[var(--text-secondary)]">
                {row.guestName}
                {row.galleryName ? ` · ${row.galleryName}` : ''}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
